import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from "@angular/common/http";
import { UntypedFormGroup } from "@angular/forms";
import { FormService } from "../../services/form.service";
import { ApiService } from "../../services/api.service";
import { TrackingService } from "../../services/tracking.service";

const MAX_FILES = 10;
const MAX_TOTAL_BYTES = 25 * 1024 * 1024;

/** Set true to bypass client-side validation and test server error handling */
const BYPASS_CLIENT_VALIDATION = false;

const ALLOWED_IMAGE_TYPES = [ 'image/jpeg', 'image/png', 'image/webp' ];

/** Error codes from backend; messages are built via getUploadErrorMessage with i18n */

export interface UploadedFile {
	tmpPath: string;
	originalName: string;
	size?: number;
}

@Component( {
	selector: 'app-upload-pictures',
	templateUrl: './upload-pictures.component.html',
	styleUrls: ['./upload-pictures.component.scss'],
} )
export class UploadPicturesComponent implements OnInit, OnDestroy {

	uploadPictures!: UntypedFormGroup;
	formGroupName: string = 'uploadPictures';

	/** Exposed for i18n in template */
	maxImages = MAX_FILES;
	maxSize = '25 MB';

	private getUploadErrorMessage( code: string ): string {

		const map: Record<string, () => string> = {
			invalid_image_type: () => $localize`Please upload only JPG, PNG, or WebP images.`,
			size_limit_reached: () => $localize`You've reached the ${ this.maxSize }:limit: limit.`,
			file_count_limit_reached: () => $localize`Maximum ${ this.maxImages }:count: photos allowed.`,
		};
		return map[code]?.() ?? code;
	}

	/** Object URLs for image previews; kept in sync with selectedFiles for cleanup */
	private previewUrls: string[] = [];

	/** Queue of files waiting to upload */
	private pendingFiles: File[] = [];

	/** Currently uploading file and its preview URL */
	uploadingFile: { file: File; previewUrl: string } | null = null;

	/** Error message shown when upload fails or files are rejected (size/format) */
	uploadErrorMessage: string | null = null;

	/** True when a file is being dragged over the upload zone */
	isDragOver: boolean = false;

	private hasTrackedUploadInteract: boolean = false;

	constructor(
		public formService: FormService,
		private apiService: ApiService,
		private trackingService: TrackingService
	) {
	}

	ngOnInit(): void {

		this.uploadPictures = this.formService.mainForm.get( this.formGroupName ) as UntypedFormGroup;

		const sessionId = this.uploadPictures.controls['_uploadSessionId'].value;
		if( ! sessionId ) {

			this.uploadPictures.controls['_uploadSessionId'].setValue( this.generateUUID() );
		}
	}

	ngOnDestroy(): void {

		this.revokeAllPreviews();
		if( this.uploadingFile ) {

			URL.revokeObjectURL( this.uploadingFile.previewUrl );
		}
	}

	private generateUUID(): string {

		if( typeof crypto !== 'undefined' && crypto.randomUUID ) {

			return crypto.randomUUID();
		}

		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace( /[xy]/g, ( c ) => {

			const r = Math.random() * 16 | 0;
			const v = c === 'x' ? r : ( r & 0x3 | 0x8 );
			return v.toString( 16 );
		} );
	}

	private trackUploadInteract(): void {

		if( this.hasTrackedUploadInteract ) {

			return;
		}

		this.trackingService.clarityEvent( 'images_upload_field_interact' );
		this.hasTrackedUploadInteract = true;
	}

	onFileSelect( event: Event ): void {

		this.trackUploadInteract();
		const input = event.target as HTMLInputElement;
		const allFiles = input.files ? Array.from( input.files ) : [];
		const added = BYPASS_CLIENT_VALIDATION
			? allFiles.filter( f => f.type.startsWith( 'image/' ) )
			: allFiles.filter( f => ALLOWED_IMAGE_TYPES.includes( f.type ) );

		if( !BYPASS_CLIENT_VALIDATION && allFiles.length > 0 && added.length < allFiles.length ) {

			this.uploadErrorMessage = this.getUploadErrorMessage( 'invalid_image_type' );
			input.value = '';
			return;
		}
		if( added.length === 0 ) {

			input.value = '';
			return;
		}

		this.uploadErrorMessage = null;
		this.addFilesToQueue( added );
		input.value = '';
	}

	onDrop( event: DragEvent ): void {

		this.trackUploadInteract();
		event.preventDefault();
		event.stopPropagation();
		this.isDragOver = false;
		const allFiles = event.dataTransfer?.files ? Array.from( event.dataTransfer.files ) : [];
		const added = BYPASS_CLIENT_VALIDATION
			? allFiles.filter( f => f.type.startsWith( 'image/' ) )
			: allFiles.filter( f => ALLOWED_IMAGE_TYPES.includes( f.type ) );

		if( !BYPASS_CLIENT_VALIDATION && allFiles.length > 0 && added.length < allFiles.length ) {

			this.uploadErrorMessage = this.getUploadErrorMessage( 'invalid_image_type' );
			return;
		}
		if( added.length === 0 ) {

			return;
		}

		this.uploadErrorMessage = null;
		this.addFilesToQueue( added );
	}

	onDragOver( event: DragEvent ): void {

		event.preventDefault();
		event.stopPropagation();
		event.dataTransfer!.dropEffect = 'copy';
	}

	onDragEnter( event: DragEvent ): void {

		event.preventDefault();
		event.stopPropagation();
		if( event.dataTransfer?.types?.includes( 'Files' ) ) {

			this.isDragOver = true;
		}
	}

	onDragLeave( event: DragEvent ): void {

		event.preventDefault();
		event.stopPropagation();
		const related = event.relatedTarget as Node | null;
		if( !related || !( event.currentTarget as Node ).contains( related ) ) {

			this.isDragOver = false;
		}
	}

	private addFilesToQueue( added: File[] ): void {

		const current = ( this.uploadPictures.controls['files'].value || [] ) as UploadedFile[];
		const unique = this.filterDuplicateFiles( current, added );
		if( unique.length === 0 ) {

			return;
		}

		const totalCount = current.length + this.pendingFiles.length + ( this.uploadingFile ? 1 : 0 );
		const countCap = unique.slice( 0, Math.max( 0, MAX_FILES - totalCount ) );
		const filesToProcess = BYPASS_CLIENT_VALIDATION ? unique : countCap;

		if( !BYPASS_CLIENT_VALIDATION && unique.length > countCap.length ) {

			this.uploadErrorMessage = this.getUploadErrorMessage( 'file_count_limit_reached' );
			if( countCap.length === 0 ) {

				return;
			}
		}

		let currentTotal = this.getCurrentTotalBytes();
		const toAdd: File[] = [];

		for( const file of filesToProcess ) {

			if( !BYPASS_CLIENT_VALIDATION && currentTotal + file.size > MAX_TOTAL_BYTES ) {

				this.uploadErrorMessage = this.getUploadErrorMessage( 'size_limit_reached' );
				break;
			}
			toAdd.push( file );
			currentTotal += file.size;
		}

		for( const file of toAdd ) {

			this.pendingFiles.push( file );
		}

		this.processQueue();
	}

	private getCurrentTotalBytes(): number {

		const current = ( this.uploadPictures.controls['files'].value || [] ) as UploadedFile[];
		const currentSize = current.reduce( ( sum, f ) => sum + ( f.size ?? 0 ), 0 );
		const pendingSize = this.pendingFiles.reduce( ( sum, f ) => sum + f.size, 0 );
		const uploadingSize = this.uploadingFile ? this.uploadingFile.file.size : 0;
		return currentSize + pendingSize + uploadingSize;
	}

	private filterDuplicateFiles( current: UploadedFile[], added: File[] ): File[] {

		const existingKeys = new Set<string>();
		const toKey = ( name: string, size: number ) => `${name}|${size}`;

		for( const f of current ) {

			existingKeys.add( toKey( f.originalName, f.size ?? 0 ) );
		}
		for( const f of this.pendingFiles ) {

			existingKeys.add( toKey( f.name, f.size ) );
		}
		if( this.uploadingFile ) {

			existingKeys.add( toKey( this.uploadingFile.file.name, this.uploadingFile.file.size ) );
		}
		return added.filter( file => ! existingKeys.has( toKey( file.name, file.size ) ) );
	}

	private processQueue(): void {

		if( this.uploadingFile || this.pendingFiles.length === 0 ) {

			return;
		}

		const file = this.pendingFiles.shift()!;
		const previewUrl = URL.createObjectURL( file );
		this.uploadingFile = { file, previewUrl };

		const sessionId = this.uploadPictures.controls['_uploadSessionId'].value;
		this.apiService.uploadTmpImage( file, sessionId ).subscribe( {
			next: ( result ) => {

				const current = ( this.uploadPictures.controls['files'].value || [] ) as UploadedFile[];
				const next = [ ...current, { tmpPath: result.tmpPath, originalName: result.originalName, size: file.size } ];
				this.uploadPictures.controls['files'].setValue( next );
				this.previewUrls.push( this.uploadingFile!.previewUrl );
				this.uploadingFile = null;
				this.processQueue();
			},
			error: ( err: HttpErrorResponse ) => {

				const code = err?.error?.error;
				if( typeof code === 'string' ) {

					const knownCodes = [ 'invalid_image_type', 'size_limit_reached', 'file_count_limit_reached' ];
					if( knownCodes.includes( code ) ) {

						this.uploadErrorMessage = this.getUploadErrorMessage( code );
					}
				}
				URL.revokeObjectURL( this.uploadingFile!.previewUrl );
				this.uploadingFile = null;
				this.processQueue();
			},
		} );
	}

	removeFile( index: number ): void {

		this.uploadErrorMessage = null;
		const current = ( this.uploadPictures.controls['files'].value || [] ) as UploadedFile[];
		const fileToRemove = current[index];

		if( fileToRemove?.tmpPath ) {

			const sessionId = this.uploadPictures.controls['_uploadSessionId'].value;
			this.apiService.deleteTmpImage( fileToRemove.tmpPath, sessionId ).subscribe();
		}

		if( this.previewUrls[index] ) {

			URL.revokeObjectURL( this.previewUrls[index] );
		}
		this.previewUrls.splice( index, 1 );
		const next = current.filter( ( _, i ) => i !== index );
		this.uploadPictures.controls['files'].setValue( next );
	}

	private revokeAllPreviews(): void {

		for( const url of this.previewUrls ) {

			if( url ) {

				URL.revokeObjectURL( url );
			}
		}
		this.previewUrls = [];
	}

	get selectedFiles(): UploadedFile[] {

		const control = this.uploadPictures?.controls['files'];
		return ( control?.value || [] ) as UploadedFile[];
	}

	get acceptTypes(): string {

		return BYPASS_CLIENT_VALIDATION ? 'image/*' : 'image/jpeg,image/png,image/webp';
	}

	trackByPreviewItem( index: number, item: { file: UploadedFile; previewUrl: string; isUploading: boolean } ): string {

		return item.file.tmpPath || `uploading-${index}`;
	}

	get displayItems(): Array<{ file: UploadedFile; previewUrl: string; isUploading: boolean }> {

		const items: Array<{ file: UploadedFile; previewUrl: string; isUploading: boolean }> = [];
		const files = this.selectedFiles;

		for( let i = 0; i < files.length; i++ ) {

			items.push( {
				file: files[i],
				previewUrl: this.previewUrls[i] || '',
				isUploading: false,
			} );
		}

		if( this.uploadingFile ) {

			items.push( {
				file: { tmpPath: '', originalName: this.uploadingFile.file.name },
				previewUrl: this.uploadingFile.previewUrl,
				isUploading: true,
			} );
		}

		return items;
	}

	getPreviewUrl( index: number ): string {

		return this.previewUrls[index] || '';
	}

	onSubmit(): void {

		this.formService.submitFormGroup( this.uploadPictures );
	}

}
